import { PnlBenchmark } from 'utils/PnL';
import { WidgetConfigWithHideableToolbar } from 'widgets/WidgetToolbarShowHidePanel/WidgetConfigWithHideableToolbar';

export enum FillsChartType {
    OrderFills = 'OrderFills',
    ExpectedVsActual = 'ExpectedVsActual',
    PercentageOfMarketVolume = 'PercentageOfMarketVolume',
    PnL = 'PnL',
    PerformanceVsIndex = 'PerformanceVsIndex',
}

export type CompanionChartTypes = Exclude<
    FillsChartType,
    FillsChartType.OrderFills
>;

export interface FillsChart {
    type: FillsChartType;
}

export interface OrderFillsConfig {
    displayOrderFillSize: boolean;
    displayVWAP: boolean;
    displayAvgExecution: boolean;
    displayPrice: boolean;
    displayPriceCandles: boolean;
    hidePriceCandleLegends: boolean;
    displayPercentageCompleted: boolean;
    displayArrival: boolean;
    displayClose: boolean;
    hideOpen: boolean;
    displayAlgorithmParametersRevisions: boolean;
    hideArrivalAndAndLabels?: boolean;
    displayLimitPrice: boolean;
    displayIndex: boolean;
    hidePriceLegend: boolean;
    hideAvgExecutionLegend: boolean;
    hideVWAPLegend: boolean;
    hidePercentageCompletedLegend: boolean;
    hideIndexLegend: boolean;
    hideFillPriceLegend: boolean;
    hideFillVolumeLegend: boolean;
    avgExecAndVwapDecimals: number;
}

export interface CollapsableCompanionChartConfig {
    collapsed?: boolean;
}

export type CollapsableCompanionChartType =
    | FillsChartType.ExpectedVsActual
    | FillsChartType.PercentageOfMarketVolume
    | FillsChartType.PnL
    | FillsChartType.PerformanceVsIndex;

export interface ExpectedVsActualConfig
    extends CollapsableCompanionChartConfig {
    displayExpected: boolean;
    displayActual: boolean;
    autoHideLastBucket: boolean;
    autoHideLastBucketThresholdPercent: number;
    hideExpectedLegend: boolean;
    hideActualLegend: boolean;
}

export interface PercentageOfMarketVolumeConfig
    extends CollapsableCompanionChartConfig {
    displayPercentageOfVolume: boolean;
    displayAvgPercentageOfVolume: boolean;
    displayMinParticipationRate: boolean;
    displayMaxParticipationRate: boolean;
    hidePercentageOfVolumeLegend: boolean;
    hideAvgPercentageOfVolumeLegend: boolean;
}

export interface PnLConfig extends CollapsableCompanionChartConfig {
    pnlChartBenchmark: PnlBenchmark;
    displayUnrealizedPnL: boolean;
    displayRealizedPnL: boolean;
    displayTotalPnL: boolean;
    hideRealizedPnLLegend: boolean;
    hideUnrealizedPnLLegend: boolean;
    hideTotalPnLLegend: boolean;
}

export interface PerformanceVsIndexConfig
    extends CollapsableCompanionChartConfig {
    displayPerformanceVsIndex: boolean;
    displayIndex: boolean;
    hidePerformanceVsIndexLegend: boolean;
    hideIndexLegend: boolean;
}

export interface FillsGraphGeneralConfig {
    displayExtendedHours: boolean;
    barSizeInMinutes: number;
    useSecondarySymbol: boolean;
    fallbackIndex: string;
}
export const initialFillsGraphGeneralConfig: FillsGraphGeneralConfig = {
    displayExtendedHours: false,
    barSizeInMinutes: 10,
    useSecondarySymbol: true,
    fallbackIndex: '',
};

export interface FillsGraphConfiguration
    extends WidgetConfigWithHideableToolbar {
    dockPercentage: number;
    general: FillsGraphGeneralConfig;
    visibleCharts: FillsChart[];
    [FillsChartType.OrderFills]: OrderFillsConfig;
    [FillsChartType.ExpectedVsActual]: ExpectedVsActualConfig;
    [FillsChartType.PercentageOfMarketVolume]: PercentageOfMarketVolumeConfig;
    [FillsChartType.PnL]: PnLConfig;
    [FillsChartType.PerformanceVsIndex]: PerformanceVsIndexConfig;
}

export const defaultFillsGraphConfiguration: FillsGraphConfiguration = {
    dockPercentage: 50,
    visibleCharts: [
        { type: FillsChartType.OrderFills },
        { type: FillsChartType.ExpectedVsActual },
        { type: FillsChartType.PercentageOfMarketVolume },
        { type: FillsChartType.PnL },
        { type: FillsChartType.PerformanceVsIndex },
    ],
    general: initialFillsGraphGeneralConfig,
    [FillsChartType.OrderFills]: {
        displayOrderFillSize: true,
        displayPrice: true,
        displayPriceCandles: false,
        hidePriceCandleLegends: false,
        displayVWAP: true,
        displayAvgExecution: true,
        displayArrival: true,
        displayPercentageCompleted: true,
        displayClose: true,
        displayAlgorithmParametersRevisions: true,
        displayLimitPrice: true,
        displayIndex: true,
        hideAvgExecutionLegend: false,
        hideIndexLegend: false,
        hidePercentageCompletedLegend: false,
        hidePriceLegend: false,
        hideVWAPLegend: false,
        hideFillPriceLegend: false,
        hideFillVolumeLegend: false,
        hideOpen: false,
        avgExecAndVwapDecimals: 4,
    },
    [FillsChartType.ExpectedVsActual]: {
        autoHideLastBucket: true,
        autoHideLastBucketThresholdPercent: 20,
        displayActual: true,
        displayExpected: true,
        hideActualLegend: false,
        hideExpectedLegend: false,
    },
    [FillsChartType.PercentageOfMarketVolume]: {
        displayPercentageOfVolume: true,
        displayAvgPercentageOfVolume: true,
        displayMinParticipationRate: true,
        displayMaxParticipationRate: true,
        hideAvgPercentageOfVolumeLegend: false,
        hidePercentageOfVolumeLegend: false,
    },
    [FillsChartType.PnL]: {
        pnlChartBenchmark: PnlBenchmark.Arrival,
        displayRealizedPnL: true,
        displayTotalPnL: true,
        displayUnrealizedPnL: true,
        hideRealizedPnLLegend: false,
        hideTotalPnLLegend: false,
        hideUnrealizedPnLLegend: false,
    },
    [FillsChartType.PerformanceVsIndex]: {
        displayPerformanceVsIndex: true,
        displayIndex: true,
        hideIndexLegend: false,
        hidePerformanceVsIndexLegend: false,
    },
};

export function toggleConfigProperty<
    T extends keyof Omit<
        Omit<FillsGraphConfiguration, 'hideToolbar'>,
        'dockPercentage'
    >,
>(
    fillsGraphConfiguration: FillsGraphConfiguration,
    section: T,
    property: keyof FillsGraphConfiguration[T]
) {
    return {
        ...fillsGraphConfiguration,
        [section]: {
            ...fillsGraphConfiguration[section],
            [property]: !fillsGraphConfiguration[section][property],
        },
    };
}
